import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';
import { useCan } from '@/auth/AuthContext';

/**
 * Справочник «DDS-статьи» — 5 строк с кодом 1С и парой бухгалтерских счетов.
 * Используется в Node-сервисе при отправке платежа в 1С (sync_payment_to_onec).
 *
 * Имена статей жёстко прошиты в коде, поэтому здесь только редактирование
 * существующих 5 строк — без создания/удаления.
 */
export default function DdsArticlesPanel() {
  const can = useCan();
  const canWrite = can('settings.dds', 'write');

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [savingId, setSavingId] = useState(null);
  // Локальные правки: { [id]: { link1c, accountSettlement, accountAdvances } }
  const [edits, setEdits] = useState({});

  const reload = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/api/dds-articles');
      setRows(res.rows || []);
      setEdits({});
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Не удалось загрузить');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const setEdit = (id, patch) => setEdits((m) => ({ ...m, [id]: { ...(m[id] || {}), ...patch } }));

  const onSave = async (row) => {
    const e = edits[row.id] || {};
    const payload = {};
    if (e.link1c !== undefined            && e.link1c            !== row.link1c)            payload.link_1c = e.link1c;
    if (e.accountSettlement !== undefined && e.accountSettlement !== row.accountSettlement) payload.accountSettlement = e.accountSettlement;
    if (e.accountAdvances !== undefined   && e.accountAdvances   !== row.accountAdvances)   payload.accountAdvances   = e.accountAdvances;
    if (e.title !== undefined             && e.title             !== row.title)             payload.title             = e.title;
    if (Object.keys(payload).length === 0) return;
    setSavingId(row.id); setError(null);
    try {
      const res = await api.put('/api/dds-articles/' + row.id, payload);
      setRows((rs) => rs.map((x) => x.id === row.id ? res.row : x));
      setEdits((m) => { const n = { ...m }; delete n[row.id]; return n; });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSavingId(null);
    }
  };

  return (
    <div className="settings-form">
      <h3 style={{ margin: '0 0 6px', fontSize: 16, fontWeight: 600 }}>Статьи ДДС (для отправки платежей в 1С)</h3>
      <div style={{ fontSize: 12, color: 'var(--fg-muted)', marginBottom: 12 }}>
        Каждой группе начислений в платеже соответствует своя статья ДДС в 1С + два счёта (расчётов и авансов).
        Эти значения подмешиваются в платёжный документ при отправке (см. Node-сервис <code>sync_payment_to_onec</code>).
        Имя статьи нельзя менять — оно прошито в коде; редактируется заголовок и три кода.
      </div>

      {error && <div className="sd-edit__error" style={{ marginBottom: 8 }}>{error}</div>}
      {loading && <div className="ref-empty">Загрузка…</div>}

      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 200 }}>Имя</th>
              <th>Заголовок</th>
              <th style={{ width: 130 }}>Код 1С (статья)</th>
              <th style={{ width: 110 }}>Счёт расчётов</th>
              <th style={{ width: 110 }}>Счёт авансов</th>
              {canWrite && <th style={{ width: 110 }} />}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const e = edits[r.id] || {};
              const titleVal       = e.title             ?? r.title;
              const link1cVal      = e.link1c            ?? r.link1c;
              const settlementVal  = e.accountSettlement ?? r.accountSettlement;
              const advancesVal    = e.accountAdvances   ?? r.accountAdvances;
              const dirty = Object.keys(e).some((k) => e[k] !== undefined && e[k] !== r[k]);
              return (
                <tr key={r.id}>
                  <td className="mono" style={{ fontSize: 12 }}>{r.name}</td>
                  <td>
                    <input
                      className="sd-edit__input"
                      value={titleVal || ''}
                      onChange={(ev) => setEdit(r.id, { title: ev.target.value })}
                      readOnly={!canWrite}
                    />
                  </td>
                  <td>
                    <input
                      className="sd-edit__input mono"
                      value={link1cVal || ''}
                      onChange={(ev) => setEdit(r.id, { link1c: ev.target.value })}
                      readOnly={!canWrite}
                    />
                  </td>
                  <td>
                    <input
                      className="sd-edit__input mono"
                      value={settlementVal || ''}
                      onChange={(ev) => setEdit(r.id, { accountSettlement: ev.target.value })}
                      readOnly={!canWrite}
                    />
                  </td>
                  <td>
                    <input
                      className="sd-edit__input mono"
                      value={advancesVal || ''}
                      onChange={(ev) => setEdit(r.id, { accountAdvances: ev.target.value })}
                      readOnly={!canWrite}
                    />
                  </td>
                  {canWrite && (
                    <td>
                      <button
                        className="btn btn--primary btn--sm"
                        disabled={!dirty || savingId === r.id}
                        onClick={() => onSave(r)}
                      >
                        {savingId === r.id ? '…' : <><Icon name="check" size={11} /> Сохранить</>}
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
